import type { Country } from './model';

export interface AvailableTextingNumber {
  phoneNumber: string;
  displayNumber: string;
  locality: string | null;
  region: string | null;
  country: Country;
  capabilities: { sms: true; mms: boolean; voice: boolean };
}

export interface AvailableNumberSearchQuery {
  country: Country;
  areaCode?: string;
}

export interface AvailableNumberSearchResult {
  country: Country;
  areaCode?: string | null;
  numbers: AvailableTextingNumber[];
  checkedAt: string;
  reserved: false;
}

export type AvailableNumberSearch = (
  query: AvailableNumberSearchQuery,
  options?: { signal?: AbortSignal },
) => Promise<AvailableNumberSearchResult>;

export class AvailableNumberSearchError extends Error {
  constructor(message: string, public code: string, public status?: number) {
    super(message);
    this.name = 'AvailableNumberSearchError';
  }
}

function validNumber(value: unknown, country: Country): value is AvailableTextingNumber {
  if (!value || typeof value !== 'object') return false;
  const number = value as AvailableTextingNumber;
  return typeof number.phoneNumber === 'string' && /^\+1[0-9]{10}$/.test(number.phoneNumber)
    && typeof number.displayNumber === 'string' && number.displayNumber.length > 0
    && (number.locality === null || typeof number.locality === 'string')
    && (number.region === null || typeof number.region === 'string')
    && number.country === country && number.capabilities?.sms === true
    && typeof number.capabilities.mms === 'boolean' && typeof number.capabilities.voice === 'boolean';
}

export function validateNumberSearchResult(value: unknown, query: AvailableNumberSearchQuery): AvailableNumberSearchResult {
  const result = value as AvailableNumberSearchResult | null;
  if (!result || result.country !== query.country || (result.areaCode || '') !== (query.areaCode || '')
    || result.reserved !== false || typeof result.checkedAt !== 'string' || !Number.isFinite(Date.parse(result.checkedAt))
    || !Array.isArray(result.numbers) || !result.numbers.every(number => validNumber(number, query.country))) {
    throw new AvailableNumberSearchError('We couldn’t confirm these numbers. Please search again.', 'INVALID_RESPONSE');
  }
  const unique = new Map(result.numbers.map(number => [number.phoneNumber, number]));
  return { ...result, numbers: [...unique.values()].slice(0, 6) };
}

/** Explicitly injected by the local preview; importing this module makes no requests. */
export function createAvailableNumberSearch(
  endpoint = '/api/signup/available-numbers',
  fetcher: typeof fetch = fetch,
): AvailableNumberSearch {
  return async (query, options) => {
    const params = new URLSearchParams({ country: query.country });
    if (query.areaCode) params.set('areaCode', query.areaCode);
    let response: Response;
    try {
      response = await fetcher(`${endpoint}?${params.toString()}`, {
        method: 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin',
        cache: 'no-store', signal: options?.signal,
      });
    } catch (error) {
      if (options?.signal?.aborted) throw error;
      throw new AvailableNumberSearchError('We couldn’t reach number search. Please try again.', 'NETWORK_ERROR');
    }
    if (!response.ok) {
      // Never surface provider payloads or credential/configuration details in the browser.
      if (response.status === 400) throw new AvailableNumberSearchError('Check the country and three-digit area code, then try again.', 'INVALID_SEARCH', 400);
      if (response.status === 429) throw new AvailableNumberSearchError('Number search is busy. Wait a moment, then try again.', 'RATE_LIMITED', 429);
      throw new AvailableNumberSearchError('Number search is temporarily unavailable. Please try again shortly.', 'UNAVAILABLE', response.status);
    }
    let result: unknown;
    try { result = await response.json(); } catch {
      throw new AvailableNumberSearchError('We couldn’t confirm these numbers. Please search again.', 'INVALID_RESPONSE');
    }
    return validateNumberSearchResult(result, query);
  };
}
